/**
 * Key showcase cards by document id.
 * Accepts an id-keyed map or the legacy position-keyed map.
 */
export function normalizeCardsById(raw) {
  if (!raw || typeof raw !== 'object') return {};

  const byId = {};
  Object.entries(raw).forEach(([key, card]) => {
    if (!card || typeof card !== 'object') return;
    const id = card.id || key;
    const positionId = card.positionId !== undefined && card.positionId !== null && card.positionId !== ''
      ? card.positionId
      : (card.id && card.id !== key ? key : null);
    byId[id] = { ...card, id, positionId: positionId || null };
  });
  return byId;
}

export function cardsByPosition(cardsById) {
  const byPosition = {};
  Object.values(cardsById || {}).forEach((card) => {
    if (card?.positionId) byPosition[card.positionId] = card;
  });
  return byPosition;
}

export function carouselCards(cardsById) {
  return Object.values(cardsById || {})
    .filter((card) => card?.imageURL)
    .sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (aTime !== bTime) return aTime - bTime;
      return String(a.id).localeCompare(String(b.id));
    });
}

/**
 * Insert or replace a card. A card that already holds the same position
 * keeps its image and loses the position.
 */
export function upsertCard(cardsById, card) {
  if (!card?.id) return cardsById || {};
  const next = { ...(cardsById || {}) };
  if (card.positionId) {
    Object.values(next).forEach((existing) => {
      if (existing.positionId === card.positionId && existing.id !== card.id) {
        next[existing.id] = { ...existing, positionId: null };
      }
    });
  }
  next[card.id] = { ...card, positionId: card.positionId || null };
  return next;
}

export function removeCardById(cardsById, cardId) {
  if (!cardId || !cardsById?.[cardId]) return cardsById || {};
  const next = { ...cardsById };
  delete next[cardId];
  return next;
}

export function uniqueOffsets(count, radius) {
  if (count <= 1) return [0];
  const offsets = [0];
  for (let step = 1; step <= radius && offsets.length < count; step += 1) {
    offsets.push(step);
    if (offsets.length < count) offsets.unshift(-step);
  }
  return offsets;
}

const CARD_WIDTH_INCHES = 2.5;
const CARD_HEIGHT_INCHES = 3.5;
const CARD_RATIO = CARD_WIDTH_INCHES / CARD_HEIGHT_INCHES;

const SLOT_SIZES = [
  { height: 380, opacity: 1 },
  { height: 320, opacity: 0.9 },
  { height: 280, opacity: 0.8 },
  { height: 240, opacity: 0.7 },
].map((slot) => ({
  ...slot,
  width: slot.height * CARD_RATIO,
}));

function slotSize(distance) {
  return SLOT_SIZES[Math.min(distance, SLOT_SIZES.length - 1)];
}

/**
 * Place each visible card so the gap between card edges stays constant.
 * A larger card pushes its neighbors outward. If the row is wider than the
 * stage, sizes and gaps scale down together until it fits.
 */
export function coverflowLayout(offsets, {
  gap = 28,
  stageWidth = 0,
  stagePadding = 32,
  scale = 1,
  orientationForOffset = () => 'vertical',
} = {}) {
  const sorted = [...offsets].sort((a, b) => a - b);
  const specs = sorted.map((offset) => {
    const size = slotSize(Math.abs(offset));
    const horizontal = orientationForOffset(offset) === 'horizontal';
    return {
      offset,
      opacity: size.opacity,
      layoutWidth: size.width,
      layoutHeight: size.height,
      visualWidth: horizontal ? size.height : size.width,
      visualHeight: horizontal ? size.width : size.height,
    };
  });

  const centerIndex = specs.findIndex((spec) => spec.offset === 0);
  if (centerIndex === -1) return [];

  const centers = new Array(specs.length).fill(0);
  for (let index = centerIndex + 1; index < specs.length; index += 1) {
    centers[index] = centers[index - 1]
      + specs[index - 1].visualWidth / 2
      + gap
      + specs[index].visualWidth / 2;
  }
  for (let index = centerIndex - 1; index >= 0; index -= 1) {
    centers[index] = centers[index + 1]
      - (specs[index + 1].visualWidth / 2 + gap + specs[index].visualWidth / 2);
  }

  let fit = 1;
  if (stageWidth > 0 && specs.length > 0) {
    const leftEdge = centers[0] - specs[0].visualWidth / 2;
    const rightEdge = centers[centers.length - 1] + specs[specs.length - 1].visualWidth / 2;
    const span = rightEdge - leftEdge;
    const available = Math.max(0, stageWidth - stagePadding * 2);
    if (span > available && span > 0) fit = available / span;
  }

  return specs.map((spec, index) => ({
    ...spec,
    centerX: centers[index] * fit * scale,
    layoutWidth: spec.layoutWidth * fit * scale,
    layoutHeight: spec.layoutHeight * fit * scale,
    visualWidth: spec.visualWidth * fit * scale,
    visualHeight: spec.visualHeight * fit * scale,
  }));
}

export function coverflowSlots(cards, activeIndex, radius) {
  const count = cards.length;
  if (count === 0) return [];
  const safeIndex = ((activeIndex % count) + count) % count;
  return uniqueOffsets(count, radius).map((offset) => {
    const index = (safeIndex + offset + count) % count;
    return { card: cards[index], index, offset };
  });
}

function parallelLabel(parallel) {
  const name = parallel?.cardParallelName ? String(parallel.cardParallelName) : '';
  if (!name) return '';
  const print = parallel.cardPrintNumber;
  const supply = parallel.cardSupply;
  if (print != null && print !== '' && supply != null && supply !== '') {
    return `${name} ${print}/${supply}`;
  }
  return name;
}

export function carouselCaption(card) {
  if (!card) return { name: '', team: '', series: '', category: '', cardNumber: '', attributes: [], parallel: '', parallelColor: null };
  const name = Array.isArray(card.name) ? card.name.filter(Boolean).join(' / ') : (card.name || '');
  const team = Array.isArray(card.team) ? card.team.filter(Boolean).join(' / ') : (card.team || '');
  const path = Array.isArray(card.cardPath) ? card.cardPath : [];
  const attributes = Array.isArray(card.cardAttributes)
    ? card.cardAttributes.filter(Boolean).map(String)
    : [];
  return {
    name: String(name).toUpperCase(),
    team: String(team).toUpperCase(),
    series: path[3] ? String(path[3]) : '',
    category: path[5] ? String(path[5]) : '',
    cardNumber: card.cardNumber ? String(card.cardNumber) : '',
    attributes,
    parallel: parallelLabel(card.cardParallel),
    parallelColor: card.cardParallel?.cardColor || null,
  };
}
